import Decimal from 'decimal.js';
import { merge } from 'lodash';
import { PartialDeep } from 'type-fest';

import { SemanticTileKind } from '@/components/diagrams/components/Tile/Tile.utils';
import { TileVariant } from '@/components/diagrams/components/Tile/types';
import { TileNode } from '@/components/diagrams/FlowChart';
import { getGrantorRelationshipsFriendlyString } from '@/modules/clientProfiles/clientProfileUtils';
import {
  ClientOrganizationKind,
  EntityInEstateStatus,
  Maybe,
} from '@/types/schema';
import { sumDecimalJS } from '@/utils/decimalJSUtils';
import { formatCurrency } from '@/utils/formatting/currency';

import {
  EntityMap_BusinessEntityFragment,
  EntityMap_ClientProfileFragment,
  EntityMap_CltTrustFragment,
  EntityMap_CrtTrustFragment,
  EntityMap_CustodialPersonalAccountFragment,
  EntityMap_DonorAdvisedFundFragment,
  EntityMap_GrantorClientProfileFragment,
  EntityMap_GratTrustFragment,
  EntityMap_IlitTrustFragment,
  EntityMap_IndividualPersonalAccountFragment,
  EntityMap_InsurancePersonalAccountFragment,
  EntityMap_IrrevocableTrustFragment,
  EntityMap_JointPersonalAccountFragment,
  EntityMap_NodeFragment,
  EntityMap_OrganizationFragment,
  EntityMap_PrivateFoundationFragment,
  EntityMap_QprtTrustFragment,
  EntityMap_QualifiedTuitionPersonalAccountFragment,
  EntityMap_RetirementPersonalAccountFragment,
  EntityMap_RevocableTrustFragment,
  EntityMap_SlatTrustFragment,
} from '../graphql/EntityMap.generated';
import { SupportedSubtypeTypename } from '../types';
import { getGrantorBusinessEntitiesNodeId } from '../utils/normalize';

type TileFromEntity<T> = Omit<EntityMap_NodeFragment, '__typename'> & {
  subtype: Omit<EntityMap_NodeFragment['subtype'], '__typename'> &
    Omit<T, '__typename'>;
};

type EntityTileFn<T> = (entity: TileFromEntity<T>) => TileNode;

const getTileVariant = (
  inEstateStatus: Maybe<EntityInEstateStatus> | undefined | null,
  defaultVariant?: TileVariant
): TileVariant | undefined =>
  inEstateStatus === EntityInEstateStatus.InEstate
    ? TileVariant.Primary
    : defaultVariant;

const defaultTileNodeProps: Pick<TileNode, 'position' | 'type'> = {
  position: { x: 0, y: 0 },
  type: 'tile',
};

const defaultFormatCurrencyOptions: Intl.NumberFormatOptions = {
  notation: 'compact',
  currencySign: 'accounting',
};

export function individualBeneficiaryTile(
  { id, displayName, relationships }: EntityMap_ClientProfileFragment,
  opts: PartialDeep<TileNode> = {}
): TileNode {
  const lineTwo = getGrantorRelationshipsFriendlyString(relationships);

  return merge<TileNode, Partial<TileNode>>(
    {
      ...defaultTileNodeProps,
      id,
      data: {
        lineOne: displayName,
        lineTwo,
        kind: SemanticTileKind.Person,
      },
    },
    opts as Partial<TileNode>
  );
}

export function individualGrantorTile({
  id,
  displayName,
}: EntityMap_ClientProfileFragment): TileNode {
  return {
    ...defaultTileNodeProps,
    id,
    data: {
      lineOne: displayName,
      kind: SemanticTileKind.Person,
    },
  };
}

export const businessEntityTile: EntityTileFn<
  EntityMap_BusinessEntityFragment
> = ({ id, subtype }) => {
  return {
    ...defaultTileNodeProps,
    id: id,
    data: {
      lineOne: subtype.displayName,
      lineTwo: formatCurrency(
        subtype.currentValue,
        defaultFormatCurrencyOptions
      ),
      variant: TileVariant.Default,
      kind: SemanticTileKind.Entity,
    },
  };
};

export const grantorBusinessEntitiesSummaryTile = (
  { id, firstName }: EntityMap_GrantorClientProfileFragment,
  ownershipStakes: EntityMap_GrantorClientProfileFragment['ownedOwnershipStakes']
): TileNode => {
  const ownershipStakesValueSum =
    ownershipStakes?.reduce(
      (sum, { ownedValue }) => sum.plus(ownedValue),
      new Decimal(0)
    ) ?? new Decimal(0);

  return {
    ...defaultTileNodeProps,
    id: getGrantorBusinessEntitiesNodeId(id),
    data: {
      lineOne: `${firstName}'s business ownership`,
      lineTwo: formatCurrency(
        ownershipStakesValueSum,
        defaultFormatCurrencyOptions
      ),
      variant: TileVariant.Primary,
      kind: SemanticTileKind.Entity,
    },
  };
};

export function organizationBeneficiaryTile({
  id,
  kind,
  name,
}: EntityMap_OrganizationFragment): TileNode {
  return {
    ...defaultTileNodeProps,
    id,
    data: {
      lineOne: name,
      variant:
        kind === ClientOrganizationKind.CharitableOrganization
          ? TileVariant.Tertiary
          : undefined,
      kind: SemanticTileKind.Organization,
    },
  };
}

const TrustTile: EntityTileFn<
  Pick<EntityMap_GratTrustFragment, 'inEstateStatus' | 'currentValue'>
> = ({ id, subtype: { displayName, inEstateStatus, currentValue } }) => {
  return {
    ...defaultTileNodeProps,
    id,
    data: {
      lineOne: displayName,
      lineTwo: formatCurrency(currentValue, defaultFormatCurrencyOptions),
      variant: getTileVariant(inEstateStatus, TileVariant.Secondary),
      kind: SemanticTileKind.Account,
    },
  };
};

const CharitableTrustTile: typeof TrustTile = (...args) => {
  const tile = TrustTile(...args);
  tile.data.variant = TileVariant.Tertiary;
  tile.data.kind = SemanticTileKind.Account;
  return tile;
};

const PersonalAccountEntityTile: EntityTileFn<
  Pick<
    EntityMap_JointPersonalAccountFragment,
    'inEstateStatus' | 'currentValue'
  >
> = ({ id, subtype: { displayName, inEstateStatus, currentValue } }) => {
  return {
    ...defaultTileNodeProps,
    id,
    data: {
      lineOne: displayName,
      lineTwo: formatCurrency(currentValue, defaultFormatCurrencyOptions),
      variant: getTileVariant(inEstateStatus, TileVariant.Default),
      kind: SemanticTileKind.Account,
    },
  };
};

const InsurancePersonalAccountEntityTile: EntityTileFn<
  Pick<EntityMap_InsurancePersonalAccountFragment, 'currentValue' | 'policies'>
> = ({ id, subtype: { displayName, currentValue, policies } }) => {
  const totalDeathBenefit = sumDecimalJS(
    (policies || [])?.map(({ deathBenefitAmount }) => deathBenefitAmount)
  );
  return {
    ...defaultTileNodeProps,
    id,
    data: {
      lineOne: displayName,
      lineTwo: formatCurrency(currentValue, defaultFormatCurrencyOptions),
      lineThree: `Death benefit ${formatCurrency(totalDeathBenefit, { notation: 'compact' })}`,
      variant: TileVariant.Primary,
      kind: SemanticTileKind.Account,
    },
  };
};

const NonTrustCharitableEntityTile: EntityTileFn<
  Pick<EntityMap_PrivateFoundationFragment, 'inEstateStatus' | 'currentValue'>
> = ({ id, subtype: { displayName, inEstateStatus, currentValue } }) => {
  return {
    ...defaultTileNodeProps,
    id,
    data: {
      lineOne: displayName,
      lineTwo: formatCurrency(currentValue, defaultFormatCurrencyOptions),
      variant: getTileVariant(inEstateStatus, TileVariant.Tertiary),
      kind: SemanticTileKind.Organization,
    },
  };
};

const GRATTrust: EntityTileFn<
  Pick<EntityMap_GratTrustFragment, 'inEstateStatus'>
> = (entity) => {
  return TrustTile(entity);
};

const ILITTrust: EntityTileFn<EntityMap_IlitTrustFragment> = (entity) => {
  const { data, ...rest } = TrustTile(entity);
  const totalDeathBenefit = sumDecimalJS(
    entity.subtype.policies?.map(
      ({ deathBenefitAmount }) => deathBenefitAmount
    ) || []
  );
  return {
    ...rest,
    data: {
      ...data,
      lineThree: `Death benefit ${formatCurrency(totalDeathBenefit, { notation: 'compact' })}`,
      kind: SemanticTileKind.Account,
    },
  };
};

const SLATTrust: EntityTileFn<EntityMap_SlatTrustFragment> = (entity) => {
  return TrustTile(entity);
};

const RevocableTrust: EntityTileFn<EntityMap_RevocableTrustFragment> = (
  entity
) => {
  return TrustTile(entity);
};

const IrrevocableTrust: EntityTileFn<EntityMap_IrrevocableTrustFragment> = (
  entity
) => {
  return TrustTile(entity);
};

const QPRTTrust: EntityTileFn<EntityMap_QprtTrustFragment> = (entity) => {
  return TrustTile(entity);
};

const IndividualPersonalAccount: EntityTileFn<
  EntityMap_IndividualPersonalAccountFragment
> = (entity) => {
  return PersonalAccountEntityTile(entity);
};

const InsurancePersonalAccount: EntityTileFn<
  EntityMap_InsurancePersonalAccountFragment
> = (entity) => {
  return InsurancePersonalAccountEntityTile(entity);
};

const JointPersonalAccount: EntityTileFn<
  EntityMap_JointPersonalAccountFragment
> = (entity) => {
  return PersonalAccountEntityTile(entity);
};

const RetirementPersonalAccount: EntityTileFn<
  EntityMap_RetirementPersonalAccountFragment
> = (entity) => {
  return PersonalAccountEntityTile(entity);
};

const CustodialPersonalAccount: EntityTileFn<
  EntityMap_CustodialPersonalAccountFragment
> = (entity) => {
  return PersonalAccountEntityTile(entity);
};

const QualifiedTuitionPersonalAccount: EntityTileFn<
  EntityMap_QualifiedTuitionPersonalAccountFragment
> = (entity) => {
  return PersonalAccountEntityTile(entity);
};

const CLTTrust: EntityTileFn<EntityMap_CltTrustFragment> = (entity) => {
  return CharitableTrustTile(entity);
};

const CRTTrust: EntityTileFn<EntityMap_CrtTrustFragment> = (entity) => {
  return CharitableTrustTile(entity);
};

const DonorAdvisedFund: EntityTileFn<EntityMap_DonorAdvisedFundFragment> = (
  entity
) => {
  return NonTrustCharitableEntityTile(entity);
};

const PrivateFoundation: EntityTileFn<EntityMap_PrivateFoundationFragment> = (
  entity
) => {
  return NonTrustCharitableEntityTile(entity);
};

export const entitySubtypeToTile: Record<
  SupportedSubtypeTypename,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we want to match against any EntityFileFn type
  EntityTileFn<any>
> = {
  GRATTrust,
  ILITTrust,
  SLATTrust,
  RevocableTrust,
  IrrevocableTrust,
  QPRTTrust,
  IndividualPersonalAccount,
  InsurancePersonalAccount, // TODO: Implement tile
  JointPersonalAccount,
  RetirementPersonalAccount,
  CustodialPersonalAccount,
  QualifiedTuitionPersonalAccount,
  CLTTrust,
  CRTTrust,
  DonorAdvisedFund,
  PrivateFoundation,
  CCorpBusinessEntity: businessEntityTile,
  SoleProprietorshipBusinessEntity: businessEntityTile,
  SCorpBusinessEntity: businessEntityTile,
  LPBusinessEntity: businessEntityTile,
  LLCBusinessEntity: businessEntityTile,
  GPBusinessEntity: businessEntityTile,
};
